/**
 * @voltxampere/ui — SVG → Canvas2D compiler (Sprint 12)
 *
 * Sprint 10.1 symbols expose `renderSVG(): string` returning
 * inner SVG markup. The Canvas2D renderer cannot consume SVG
 * directly, but parsing the markup *every frame* would bake
 * regex cost into the hot path (500 components × N primitives).
 *
 * This compiler runs once per symbol id, caches the resulting
 * `SymbolDrawProgram`, and hands the renderer a pre-decoded
 * primitive list. Subsequent draw calls just iterate the program.
 *
 * Supported elements (Sprint 12 inventory of the 20 symbols):
 *   `<line>`, `<circle>`, `<path>` (M/L/Q/Z commands), `<polygon>`.
 * `<text>` is intentionally skipped — Op-Amp's ±-text labels
 * arrive in Sprint 13 with the inspector wiring.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
const programCache = new Map();
/** Reset the cache — exposed for unit tests. */
export function clearProgramCache() {
    programCache.clear();
}
export function compileSymbol(id, svg) {
    const cached = programCache.get(id);
    if (cached)
        return cached;
    const compiled = {
        id,
        primitives: parseSvg(svg),
    };
    programCache.set(id, compiled);
    return compiled;
}
// ── parser ──────────────────────────────────────────────────────────
function parseSvg(svg) {
    const out = [];
    // Sprint 12 surface — `<text>` is *intentionally* skipped.
    const elementRe = /<(line|circle|path|polygon)([^>]*)\/?>/g;
    let match;
    while ((match = elementRe.exec(svg)) !== null) {
        const tag = match[1];
        const attrs = match[2] ?? "";
        const dashArray = dashArrayAttr(attrs);
        if (tag === "line") {
            out.push({
                kind: "line",
                x1: numAttr(attrs, "x1") ?? 0,
                y1: numAttr(attrs, "y1") ?? 0,
                x2: numAttr(attrs, "x2") ?? 0,
                y2: numAttr(attrs, "y2") ?? 0,
                strokeWidth: numAttr(attrs, "stroke-width") ?? 1,
                stroke: strAttr(attrs, "stroke"),
                dashArray,
            });
        }
        else if (tag === "circle") {
            out.push({
                kind: "circle",
                cx: numAttr(attrs, "cx") ?? 0,
                cy: numAttr(attrs, "cy") ?? 0,
                r: numAttr(attrs, "r") ?? 0,
                strokeWidth: numAttr(attrs, "stroke-width") ?? 1,
                stroke: strAttr(attrs, "stroke"),
                fill: strAttr(attrs, "fill"),
                dashArray,
            });
        }
        else if (tag === "path") {
            out.push({
                kind: "path",
                segments: parsePathD(strAttr(attrs, "d") ?? ""),
                strokeWidth: numAttr(attrs, "stroke-width") ?? 1,
                stroke: strAttr(attrs, "stroke"),
                fill: strAttr(attrs, "fill"),
                dashArray,
            });
        }
        else if (tag === "polygon") {
            out.push({
                kind: "polygon",
                points: parsePolygonPoints(strAttr(attrs, "points") ?? ""),
                strokeWidth: numAttr(attrs, "stroke-width") ?? 1,
                stroke: strAttr(attrs, "stroke"),
                fill: strAttr(attrs, "fill"),
                dashArray,
            });
        }
    }
    return out;
}
function strAttr(attrs, name) {
    const re = new RegExp(`${name}="([^"]+)"`);
    const m = attrs.match(re);
    const value = m?.[1];
    if (value === undefined)
        return null;
    // SVG `fill="none"` ⇒ stroke-only render — surface as null.
    if (value === "none")
        return null;
    return value;
}
function numAttr(attrs, name) {
    const raw = strAttr(attrs, name);
    if (raw === null)
        return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
}
/**
 * Parse a `stroke-dasharray="a,b"` (or whitespace-separated)
 * attribute into a `[a, b, ...]` array of finite positive numbers.
 * Returns `null` when the attribute is missing, `"none"`, or yields
 * no valid numbers (so `setLineDash([])` is the correct fallback).
 */
function dashArrayAttr(attrs) {
    const raw = strAttr(attrs, "stroke-dasharray");
    if (raw === null)
        return null;
    const tokens = raw.split(/[\s,]+/).filter((s) => s.length > 0);
    const nums = [];
    for (const tok of tokens) {
        const n = Number(tok);
        if (Number.isFinite(n) && n >= 0)
            nums.push(n);
    }
    return nums.length > 0 ? nums : null;
}
function parsePathD(d) {
    const segments = [];
    // Tokenise the d-string into command + numeric runs.
    const tokens = d.match(/[MLQZmlqz]|-?\d*\.?\d+/g);
    if (!tokens)
        return segments;
    let i = 0;
    while (i < tokens.length) {
        const cmd = tokens[i++];
        if (cmd === undefined)
            break;
        const upper = cmd.toUpperCase();
        if (upper === "M" || upper === "L") {
            const xs = tokens[i++];
            const ys = tokens[i++];
            if (xs === undefined || ys === undefined)
                break;
            segments.push({ type: upper, x: Number(xs), y: Number(ys) });
        }
        else if (upper === "Q") {
            const cxs = tokens[i++];
            const cys = tokens[i++];
            const xs = tokens[i++];
            const ys = tokens[i++];
            if (cxs === undefined || cys === undefined || xs === undefined || ys === undefined)
                break;
            segments.push({
                type: "Q",
                cpx: Number(cxs),
                cpy: Number(cys),
                x: Number(xs),
                y: Number(ys),
            });
        }
        else {
            // The matching regex restricts upper to one of M/L/Q/Z;
            // M/L/Q are handled above, so the remaining branch must be
            // Z. Tokens emitted from `H/V/C/S/T/A` paths would not
            // appear because the regex does not match those letters in
            // the first place.
            segments.push({ type: "Z" });
        }
    }
    return segments;
}
function parsePolygonPoints(raw) {
    return raw
        .trim()
        .split(/\s+/)
        .map((pair) => {
        const [xs, ys] = pair.split(",");
        return { x: Number(xs), y: Number(ys) };
    })
        .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
}
// ── runtime ─────────────────────────────────────────────────────────
/**
 * Render a compiled program. `strokeOverride` lets the caller
 * recolour every stroke uniformly (default ivory / hover brass /
 * selected brassHot, per Blueprint §12.6 selection feedback) while
 * authored fill colours pass through verbatim — the LED's
 * translucent body and the BJT emitter arrow keep their semantic
 * fills regardless of selection state.
 */
/**
 * Sprint 25 R4 lane 2 — `widthFactor` lets the caller scale the
 * symbol's per-primitive `stroke-width` without rewriting the SVG.
 * Used for hover / selection emphasis on `<vxa-canvas>` so the LED
 * and potentiometer (which hold semantic stroke colours that
 * `strokeOverride` would homogenise away) can still surface a
 * visual cue that the user has hovered or selected them. Default
 * `1` preserves the SVG-declared widths.
 */
export function renderProgram(ctx, program, strokeOverride, widthFactor = 1) {
    for (const prim of program.primitives) {
        ctx.save();
        ctx.strokeStyle = strokeOverride;
        ctx.lineWidth = prim.strokeWidth * widthFactor;
        ctx.lineCap = "square";
        ctx.lineJoin = "round";
        // Sprint 25 R4 lane 1 — apply dash pattern when the SVG primitive
        // declared `stroke-dasharray`. `setLineDash` reset is implicit
        // via `ctx.save`/`ctx.restore` so the next primitive starts solid
        // unless it also declares a pattern. (CanvasContext2DLike is
        // satisfied by both real `CanvasRenderingContext2D` and the
        // happy-dom stub, so we still type-guard the method.)
        if (prim.dashArray !== null && typeof ctx.setLineDash === "function") {
            ctx.setLineDash(Array.from(prim.dashArray));
        }
        if (prim.kind === "line") {
            ctx.beginPath();
            ctx.moveTo(prim.x1, prim.y1);
            ctx.lineTo(prim.x2, prim.y2);
            ctx.stroke();
        }
        else if (prim.kind === "circle") {
            ctx.beginPath();
            ctx.arc(prim.cx, prim.cy, prim.r, 0, Math.PI * 2);
            if (prim.fill !== null) {
                ctx.fillStyle = prim.fill;
                ctx.fill();
            }
            ctx.stroke();
        }
        else if (prim.kind === "path") {
            ctx.beginPath();
            for (const seg of prim.segments) {
                switch (seg.type) {
                    case "M":
                        ctx.moveTo(seg.x, seg.y);
                        break;
                    case "L":
                        ctx.lineTo(seg.x, seg.y);
                        break;
                    case "Q":
                        ctx.quadraticCurveTo(seg.cpx, seg.cpy, seg.x, seg.y);
                        break;
                    case "Z":
                        ctx.closePath();
                        break;
                }
            }
            if (prim.fill !== null) {
                ctx.fillStyle = prim.fill;
                ctx.fill();
            }
            ctx.stroke();
        }
        else {
            // polygon
            ctx.beginPath();
            const head = prim.points[0];
            if (head === undefined) {
                ctx.restore();
                continue;
            }
            ctx.moveTo(head.x, head.y);
            for (let i = 1; i < prim.points.length; i++) {
                const p = prim.points[i];
                if (p === undefined)
                    continue;
                ctx.lineTo(p.x, p.y);
            }
            ctx.closePath();
            if (prim.fill !== null) {
                ctx.fillStyle = prim.fill;
                ctx.fill();
            }
            ctx.stroke();
        }
        ctx.restore();
    }
}
//# sourceMappingURL=svg-compile.js.map